import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ChatBox from "@/components/ChatBox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessagesSquare, Users2, Plus, LogOut, Lock, Trash2, UserPlus } from "lucide-react";

export default function TeamChat() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  // Deep-linked from a chat push notification (?room=room_xxx) — land straight in that
  // group instead of the generic Chat Tim channel, the whole point of tapping the
  // notification in the first place.
  const [active, setActive] = useState(() => searchParams.get("room") || "team"); // "team" | room_id
  const [createOpen, setCreateOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [newMembers, setNewMembers] = useState([]);
  const [addingMembers, setAddingMembers] = useState(false);

  // Also react to the query param changing while already on /chat (route doesn't remount
  // just because the query string changed) — otherwise tapping a chat notification while
  // the app is already open on this page silently does nothing.
  React.useEffect(() => {
    const room = searchParams.get("room");
    if (room) setActive(room);
  }, [searchParams]);

  const { data: rooms = [] } = useQuery({ queryKey: ["chat-rooms"], queryFn: async () => (await api.get("/chat-rooms")).data });
  const { data: directory = [] } = useQuery({ queryKey: ["users-directory"], queryFn: async () => (await api.get("/users/directory")).data });

  const activeRoom = active !== "team" ? rooms.find((r) => r.room_id === active) : null;

  const toggleMember = (uid) => setSelectedMembers((cur) => cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid]);

  const createGroup = async () => {
    if (!groupName.trim()) { toast.error("Nama grup tidak boleh kosong"); return; }
    if (!selectedMembers.length) { toast.error("Pilih minimal 1 anggota"); return; }
    setCreating(true);
    try {
      const r = await api.post("/chat-rooms", { name: groupName.trim(), member_ids: selectedMembers });
      toast.success("Grup dibuat"); setCreateOpen(false); setGroupName(""); setSelectedMembers([]);
      qc.invalidateQueries({ queryKey: ["chat-rooms"] });
      setActive(r.data.room_id);
    } catch (e) { toast.error(apiErr(e)); } finally { setCreating(false); }
  };

  const leaveGroup = async (roomId) => {
    if (!window.confirm("Keluar dari grup ini?")) return;
    try {
      await api.post(`/chat-rooms/${roomId}/leave`);
      toast.success("Keluar dari grup"); if (active === roomId) setActive("team");
      qc.invalidateQueries({ queryKey: ["chat-rooms"] });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleNewMember = (uid) => setNewMembers((cur) => cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid]);

  const addMembers = async () => {
    if (!activeRoom || !newMembers.length) return;
    setAddingMembers(true);
    try {
      await api.post(`/chat-rooms/${activeRoom.room_id}/members`, { member_ids: newMembers });
      toast.success("Anggota ditambahkan"); setAddMembersOpen(false); setNewMembers([]);
      qc.invalidateQueries({ queryKey: ["chat-rooms"] });
    } catch (e) { toast.error(apiErr(e)); } finally { setAddingMembers(false); }
  };

  // Only the group's creator can delete it — chat history isn't auto-pruned like
  // notifications, so removing it for everyone has to be a deliberate manual call.
  const deleteGroup = async (roomId) => {
    if (!window.confirm("Hapus grup ini beserta semua riwayat percakapannya? Tidak bisa dibatalkan.")) return;
    try {
      await api.delete(`/chat-rooms/${roomId}`);
      toast.success("Grup dihapus"); if (active === roomId) setActive("team");
      qc.invalidateQueries({ queryKey: ["chat-rooms"] });
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="px-4 py-5 max-w-3xl mx-auto md:max-w-none md:px-8" data-testid="team-chat-page">
      <h1 className="text-xl font-bold font-heading tracking-tight flex items-center gap-2"><MessagesSquare className="text-blue-400" size={22} />Chat Tim</h1>
      <p className="text-sm text-zinc-500 mb-4">Chat Tim untuk semua, atau buat grup khusus supaya diskusi tidak mengganggu yang tidak diundang.</p>

      <div className="flex items-center gap-2 mb-3 overflow-x-auto hide-scrollbar pb-1">
        <button data-testid="chat-channel-team" onClick={() => setActive("team")}
          className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium border ${active === "team" ? "bg-blue-500/15 text-blue-300 border-blue-500/30" : "bg-[#18181B] text-zinc-400 border-white/10"}`}>
          <Users2 size={13} /> Chat Tim
        </button>
        {rooms.map((r) => (
          <button key={r.room_id} data-testid={`chat-channel-${r.room_id}`} onClick={() => setActive(r.room_id)}
            className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium border max-w-[160px] truncate ${active === r.room_id ? "bg-blue-500/15 text-blue-300 border-blue-500/30" : "bg-[#18181B] text-zinc-400 border-white/10"}`}>
            <Lock size={11} className="shrink-0" /> <span className="truncate">{r.name}</span>
          </button>
        ))}
        <button data-testid="chat-create-group" onClick={() => setCreateOpen(true)}
          className="shrink-0 flex items-center gap-1 px-3 py-2 rounded-full text-xs font-medium border border-dashed border-white/20 text-zinc-500 hover:text-zinc-300 hover:border-white/30">
          <Plus size={13} /> Grup Baru
        </button>
      </div>

      {activeRoom && (
        <div className="flex items-center justify-between mb-2 px-1">
          <span className="text-xs text-zinc-500">{activeRoom.member_ids.length} anggota grup</span>
          <div className="flex items-center gap-3">
            {activeRoom.can_post !== false && (
              <button onClick={() => { setNewMembers([]); setAddMembersOpen(true); }} className="text-xs text-blue-400 flex items-center gap-1 hover:underline"><UserPlus size={12} /> Tambah anggota</button>
            )}
            {activeRoom.created_by === user?.user_id && (
              <button onClick={() => deleteGroup(activeRoom.room_id)} className="text-xs text-red-400 flex items-center gap-1 hover:underline"><Trash2 size={12} /> Hapus grup</button>
            )}
            {activeRoom.can_post !== false && (
              <button onClick={() => leaveGroup(activeRoom.room_id)} className="text-xs text-red-400 flex items-center gap-1 hover:underline"><LogOut size={12} /> Keluar grup</button>
            )}
          </div>
        </div>
      )}

      <div className="card-surface rounded-2xl p-3"><ChatBox channel={active} height="calc(100dvh - 380px)" readOnly={!!activeRoom && activeRoom.can_post === false} /></div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#0f0f12] border-white/10">
          <DialogHeader><DialogTitle className="font-heading">Buat Grup Baru</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Nama grup, mis. Tim Cabang Jakarta" className="bg-[#18181B] border-white/10" />
            <div>
              <div className="text-xs text-zinc-400 uppercase mb-1.5">Undang Anggota</div>
              <div className="max-h-56 overflow-y-auto hide-scrollbar space-y-1 rounded-lg border border-white/10 p-1.5">
                {directory.map((u) => (
                  <label key={u.user_id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer text-sm">
                    <input type="checkbox" checked={selectedMembers.includes(u.user_id)} onChange={() => toggleMember(u.user_id)} className="accent-blue-600" />
                    <span>{u.name}</span>
                    <span className="text-[10px] text-zinc-500 capitalize ml-auto">{u.role}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={createGroup} disabled={creating} className="w-full bg-blue-600 hover:bg-blue-700">Buat Grup</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={addMembersOpen} onOpenChange={setAddMembersOpen}>
        <DialogContent className="bg-[#0f0f12] border-white/10">
          <DialogHeader><DialogTitle className="font-heading">Tambah Anggota — {activeRoom?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="max-h-64 overflow-y-auto hide-scrollbar space-y-1 rounded-lg border border-white/10 p-1.5">
              {directory.filter((u) => !activeRoom?.member_ids?.includes(u.user_id)).map((u) => (
                <label key={u.user_id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer text-sm">
                  <input type="checkbox" checked={newMembers.includes(u.user_id)} onChange={() => toggleNewMember(u.user_id)} className="accent-blue-600" />
                  <span>{u.name}</span>
                  <span className="text-[10px] text-zinc-500 capitalize ml-auto">{u.role}</span>
                </label>
              ))}
              {directory.filter((u) => !activeRoom?.member_ids?.includes(u.user_id)).length === 0 && (
                <p className="text-xs text-zinc-600 text-center py-4">Semua orang sudah jadi anggota grup ini</p>
              )}
            </div>
            <Button onClick={addMembers} disabled={addingMembers || !newMembers.length} className="w-full bg-blue-600 hover:bg-blue-700">Tambahkan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
